import { type FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { PullStatusType } from "@/types/graphql/partner.generated";

// Общий для форм стрима и точки входа блок pull-инжеста (spec §5). Живёт в
// partner/shared, а не в features/streams или features/streamEntrypoints:
// eslint.boundaries.mjs запрещает feature-ui одной фичи импортировать
// что-либо из другой (только entities/shared), а компонент нужен обеим.
// Сам PullBlock ничего не знает про GraphQL-мутации entities/* (shared тоже
// не имеет права их импортировать) — управление состоянием pull остаётся на
// вызывающей фиче, сюда передаются только данные и колбэки.
export interface PullBlockPull {
  sourceUrl: string;
  status: PullStatusType;
  attempt: number;
  maxAttempts: number;
  startedAt: string;
  finishedAt?: string | null;
  lastError?: { code: string; message: string } | null;
}

interface PullBlockProps {
  pull: PullBlockPull | null | undefined;
  // Cloudflare отверг бы второй коннект, если цель уже в эфире (собственный
  // push или форвард точки) — кнопка запуска блокируется на этот случай.
  liveConnected: boolean;
  starting: boolean;
  stopping: boolean;
  onStart: (sourceUrl: string) => void;
  onStop: () => void;
}

const PULL_STATUS_CHIP_COLOR: Record<
  PullStatusType,
  "success" | "warning" | "error" | "default"
> = {
  [PullStatusType.RunningType]: "success",
  [PullStatusType.RetryingType]: "warning",
  [PullStatusType.FailedType]: "error",
  [PullStatusType.StoppedType]: "default",
};

const PULL_STATUS_LABEL_KEYS: Record<PullStatusType, string> = {
  [PullStatusType.RunningType]: "pull.status.RUNNING",
  [PullStatusType.RetryingType]: "pull.status.RETRYING",
  [PullStatusType.FailedType]: "pull.status.FAILED",
  [PullStatusType.StoppedType]: "pull.status.STOPPED",
};

const ACTIVE_STATUSES: PullStatusType[] = [
  PullStatusType.RunningType,
  PullStatusType.RetryingType,
];

// Локальный хелпер: существующие shared/utils/formatDuration и
// Player/formatPlayerTime опускают часовой сегмент при значении 0
// ("m:ss"), а спека требует фиксированный HH:MM:SS.
function formatElapsed(totalSeconds: number): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export const PullBlock: FC<PullBlockProps> = ({
  pull,
  liveConnected,
  starting,
  stopping,
  onStart,
  onStop,
}) => {
  const { t } = useTranslation();
  const [sourceUrl, setSourceUrl] = useState(pull?.sourceUrl ?? "");

  // Поле — черновик URL, не отражённый в кэше до первого запуска, поэтому
  // держим его в локальном состоянии; синхронизируем только когда сервер
  // подтверждает значение (после старта поле и так задизейблено).
  useEffect(() => {
    if (pull?.sourceUrl) setSourceUrl(pull.sourceUrl);
  }, [pull?.sourceUrl]);

  const isActive = Boolean(pull && ACTIVE_STATUSES.includes(pull.status));

  // Тикающий таймер длительности — setInterval на клиенте, пока
  // RUNNING/RETRYING; поллинг/подписка обновляют startedAt с сервера, но
  // между их тиками секунды нужно докручивать локально.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isActive]);

  const elapsedSeconds =
    isActive && pull
      ? Math.max(
          0,
          Math.floor((now - new Date(pull.startedAt).getTime()) / 1000)
        )
      : null;

  const startDisabled = starting || liveConnected || !sourceUrl.trim();

  const startButton = (
    <Button
      variant="contained"
      disabled={startDisabled}
      onClick={() => onStart(sourceUrl.trim())}
    >
      {t("pull.start")}
    </Button>
  );

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t("pull.label")}
      </Typography>
      <Stack spacing={2}>
        <TextField
          fullWidth
          required
          variant="outlined"
          label={t("pull.sourceUrl")}
          value={sourceUrl}
          disabled={isActive}
          onChange={(event) => setSourceUrl(event.target.value)}
        />

        {pull && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              size="small"
              color={PULL_STATUS_CHIP_COLOR[pull.status]}
              label={
                pull.status === PullStatusType.RetryingType
                  ? `${t(PULL_STATUS_LABEL_KEYS[pull.status])} — ${t(
                      "pull.attemptOf",
                      { attempt: pull.attempt, max: pull.maxAttempts }
                    )}`
                  : t(PULL_STATUS_LABEL_KEYS[pull.status])
              }
            />
            {elapsedSeconds !== null && (
              <Typography variant="body2" color="text.secondary">
                {t("pull.duration", {
                  duration: formatElapsed(elapsedSeconds),
                })}
              </Typography>
            )}
          </Stack>
        )}

        {pull?.status === PullStatusType.FailedType && pull.lastError && (
          <Alert severity="error">{pull.lastError.message}</Alert>
        )}

        <Box>
          {isActive ? (
            <Button
              variant="outlined"
              color="error"
              disabled={stopping}
              onClick={onStop}
            >
              {t("pull.stop")}
            </Button>
          ) : liveConnected ? (
            <Tooltip title={t("pull.unavailableLive")}>
              <span>{startButton}</span>
            </Tooltip>
          ) : (
            startButton
          )}
        </Box>
      </Stack>
    </Box>
  );
};
